(function () {
  const SECTION_SELECTOR = '[data-fun-economics]';
  const instances = new Map();

  const numberFormatter = (options = {}) =>
    new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
      ...options,
    });

  const formatLargeNumber = (value) => {
    const absValue = Math.abs(value);
    const digits = absValue >= 100 ? 0 : absValue >= 10 ? 1 : 2;
    return numberFormatter({ maximumFractionDigits: digits }).format(value);
  };

  const formatPercent = (value) =>
    numberFormatter({ maximumFractionDigits: value >= 10 ? 0 : 1 }).format(value);

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const toFiniteNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const computeFpd = ({
    engagementHours = 0,
    participants = 0,
    reuseFactor = 0,
    sensoryDepth = 0,
    cost = 0,
    noveltyDecay = 1,
    groupSynergy = 1,
  }) => {
    const baseNumerator = engagementHours * participants * reuseFactor * sensoryDepth;
    const modifiedNumerator = baseNumerator * noveltyDecay * groupSynergy;
    return cost > 0 ? modifiedNumerator / cost : 0;
  };

  const computeNoveltyDecay = (reuseFactor, initialEngagement, decayRate = 0.15) => {
    if (reuseFactor <= 1) return 1;
    const decayExponent = reuseFactor - 1;
    return Math.max(0.3, 1 - (decayRate * decayExponent));
  };

  const computeGroupSynergy = (participants, synergyBase = 0.1, synergyCap = 1.5) => {
    if (participants <= 1) return 1;
    const synergyMultiplier = 1 + (synergyBase * Math.log(participants));
    return Math.min(synergyCap, synergyMultiplier);
  };

  const computeFvs = (fpd, maxFpd) => (maxFpd > 0 ? (fpd / maxFpd) * 100 : 0);

  const datasetCache = new Map();

  const fetchBaselineDataset = (url) => {
    if (!url) {
      return Promise.resolve(null);
    }

    if (datasetCache.has(url)) {
      return datasetCache.get(url);
    }

    const promise = fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        return response.json();
      })
      .catch((error) => {
        console.warn('Fun Economics: Failed to load baseline dataset', error);
        return null;
      });

    datasetCache.set(url, promise);
    return promise;
  };

  const repairMalformedBaselineConfig = (raw) => {
    if (typeof raw !== 'string' || !raw.trim()) {
      return raw;
    }

    let cleaned = raw;

    cleaned = cleaned.replace(/\\"([a-zA-Z0-9_]+)\\":/g, '"$1":');

    cleaned = cleaned.replace(/"([a-zA-Z0-9_]+)":([A-Za-z0-9_]+)(?=[,}])/g, (_match, key, value) => {
      return `"${key}":"${value}"`;
    });

    cleaned = cleaned.replace(/"label":([^",}]+)(?=",)/g, (_match, value) => {
      return `"label":${JSON.stringify(value.trim())}`;
    });

    return cleaned;
  };

  const parseBaselineConfig = (container) => {
    const script = container.querySelector('[data-fpd-baseline-config]');
    if (!script) {
      return [];
    }

    const raw = script.textContent || '[]';

    const tryParse = (value) => {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : null;
      } catch (error) {
        return null;
      }
    };

    let data = tryParse(raw);

    if (!data) {
      const repaired = repairMalformedBaselineConfig(raw);
      if (repaired && repaired !== raw) {
        data = tryParse(repaired);
      }
    }

    if (!data) {
      console.warn('Fun Economics: Failed to parse baseline configuration', { raw });
      return [];
    }

    return data.map((item) => ({
      id: item.id,
      scenarioId: typeof item.scenarioId === 'string' ? item.scenarioId : '',
      label: typeof item.label === 'string' ? item.label.trim() : '',
      fallbackMetrics: {
        engagementHours: toFiniteNumber(item?.fallbackMetrics?.engagementHours),
        participants: toFiniteNumber(item?.fallbackMetrics?.participants),
        reuseFactor: toFiniteNumber(item?.fallbackMetrics?.reuseFactor),
        sensoryDepth: toFiniteNumber(item?.fallbackMetrics?.sensoryDepth),
        cost: toFiniteNumber(item?.fallbackMetrics?.cost),
      },
    }));
  };

  const buildBaselines = ({ configEntries, dataset }) => {
    const referencesMap = new Map(
      (dataset?.references || []).map((reference) => [reference.id, reference])
    );

    const datasetInfo = {
      updatedAt: dataset?.updated_at || '',
      notes: dataset?.notes || '',
      compiledBy: dataset?.compiled_by || '',
    };

    const baselines = configEntries.map((entry) => {
      const scenario = (dataset?.scenarios || []).find(
        (item) => item.id === entry.scenarioId
      );

      const metrics = {
        engagementHours: toFiniteNumber(
          scenario?.metrics?.engagement_hours ?? entry.fallbackMetrics.engagementHours
        ),
        participants: toFiniteNumber(
          scenario?.metrics?.participants ?? entry.fallbackMetrics.participants
        ),
        reuseFactor: toFiniteNumber(
          scenario?.metrics?.reuse_factor ?? entry.fallbackMetrics.reuseFactor
        ),
        sensoryDepth: toFiniteNumber(
          scenario?.metrics?.sensory_depth ?? entry.fallbackMetrics.sensoryDepth
        ),
        cost: toFiniteNumber(
          scenario?.metrics?.cost ?? entry.fallbackMetrics.cost
        ),
      };

      const references = (scenario?.sources || [])
        .map((referenceId) => referencesMap.get(referenceId))
        .filter(Boolean);

      const baseline = {
        id: entry.id,
        scenarioId: entry.scenarioId,
        label: entry.label || scenario?.name || 'Baseline scenario',
        ...metrics,
        metrics,
        fpd: computeFpd(metrics),
        assumptions: scenario?.assumptions || '',
        references,
      };

      return baseline;
    });

    return { baselines, datasetInfo };
  };

  const updateBaselineLabels = (container, baselines) => {
    const select = container.querySelector('[data-fpd-compare]');

    baselines.forEach((baseline) => {
      const option = select?.querySelector(`option[value="${baseline.id}"]`);
      if (option) {
        option.textContent = baseline.label;
      }

      const barLabel = container.querySelector(
        `[data-fpd-bar-label="${baseline.id}"]`
      );
      if (barLabel) {
        barLabel.textContent = baseline.label;
      }

      const rowLabel = container.querySelector(
        `[data-fpd-row-label="${baseline.id}"]`
      );
      if (rowLabel) {
        rowLabel.textContent = baseline.label;
      }
    });
  };

  const formatDatasetDate = (value) => {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const updateDatasetPanel = ({ container, datasetInfo, baseline }) => {
    const wrapper = container.querySelector('[data-fpd-sources]');
    if (!wrapper) {
      return;
    }

    const updatedEl = wrapper.querySelector('[data-fpd-baseline-updated]');
    const notesEl = wrapper.querySelector('[data-fpd-baseline-notes]');
    const assumptionsEl = wrapper.querySelector('[data-fpd-baseline-assumptions]');
    const sourceListEl = wrapper.querySelector('[data-fpd-source-list]');

    if (updatedEl) {
      if (datasetInfo.updatedAt) {
        updatedEl.textContent = `Last validated ${formatDatasetDate(datasetInfo.updatedAt)}`;
      } else {
        updatedEl.textContent = 'Baseline metadata pending';
      }
    }

    if (notesEl) {
      notesEl.textContent = datasetInfo.notes || '';
      notesEl.classList.toggle('is-hidden', !datasetInfo.notes);
    }

    if (assumptionsEl) {
      const hasAssumptions = Boolean(baseline?.assumptions);
      assumptionsEl.textContent = hasAssumptions
        ? `Scenario assumptions: ${baseline.assumptions}`
        : '';
      assumptionsEl.classList.toggle('is-hidden', !hasAssumptions);
    }

    if (sourceListEl) {
      sourceListEl.innerHTML = '';

      const references = baseline?.references || [];
      if (!references.length) {
        const emptyItem = document.createElement('li');
        emptyItem.textContent = 'Source references will appear here once documented.';
        sourceListEl.appendChild(emptyItem);
      } else {
        references.forEach((reference) => {
          const li = document.createElement('li');

          if (reference.url) {
            const anchor = document.createElement('a');
            anchor.href = reference.url;
            anchor.target = '_blank';
            anchor.rel = 'noopener noreferrer';
            anchor.textContent = reference.title || reference.id;
            li.appendChild(anchor);
          } else {
            li.textContent = reference.title || reference.id || 'Reference';
          }

          if (reference.summary) {
            const summary = document.createElement('p');
            summary.textContent = reference.summary;
            li.appendChild(summary);
          }

          sourceListEl.appendChild(li);
        });
      }
    }
  };

  const readMetricsFromForm = (form) => {
    const getValue = (fieldId) => {
      const field = form.querySelector(`[data-fpd-field="${fieldId}"]`);
      return field ? parseFloat(field.value) : NaN;
    };

    const getCheckboxValue = (fieldId) => {
      const field = form.querySelector(`[data-fpd-field="${fieldId}"]`);
      return field ? field.checked : false;
    };

    const baseMetrics = {
      engagementHours: getValue('engagementHours'),
      participants: getValue('participants'),
      reuseFactor: getValue('reuseFactor'),
      sensoryDepth: getValue('sensoryDepth'),
      cost: getValue('cost'),
    };

    const useAdvancedModifiers = getCheckboxValue('useAdvancedModifiers');
    
    if (useAdvancedModifiers) {
      const noveltyDecayRate = getValue('noveltyDecayRate') || 0.15;
      const groupSynergyBase = getValue('groupSynergyBase') || 0.1;
      
      const noveltyDecay = computeNoveltyDecay(baseMetrics.reuseFactor, baseMetrics.engagementHours, noveltyDecayRate);
      const groupSynergy = computeGroupSynergy(baseMetrics.participants, groupSynergyBase);
      
      return {
        ...baseMetrics,
        noveltyDecay,
        groupSynergy,
        useAdvancedModifiers: true,
      };
    }

    return {
      ...baseMetrics,
      noveltyDecay: 1,
      groupSynergy: 1,
      useAdvancedModifiers: false,
    };
  };

  const validateMetrics = (metrics) => {
    const errors = [];

    if (!Number.isFinite(metrics.engagementHours) || metrics.engagementHours <= 0) {
      errors.push('Estimated total play hours (E) must be greater than 0.');
    }

    if (!Number.isFinite(metrics.participants) || metrics.participants <= 0) {
      errors.push('Number of simultaneous participants (N) must be greater than 0.');
    }

    if (!Number.isFinite(metrics.reuseFactor) || metrics.reuseFactor <= 0) {
      errors.push('Reuse cycles (R) must be greater than 0.');
    }

    if (!Number.isFinite(metrics.sensoryDepth) || metrics.sensoryDepth <= 0) {
      errors.push('Sensory depth (S) must be greater than 0.');
    }

    if (!Number.isFinite(metrics.cost) || metrics.cost <= 0) {
      errors.push('Cost (C) must be greater than 0.');
    }

    return errors;
  };

  const updateErrorState = (container, errors) => {
    const errorContainer = container.querySelector('[data-fpd-errors]');
    if (!errorContainer) {
      return;
    }

    const list = errorContainer.querySelector('ul');
    if (!list) {
      return;
    }

    list.innerHTML = '';

    if (!errors.length) {
      errorContainer.classList.remove('is-visible');
      return;
    }

    errors.forEach((error) => {
      const li = document.createElement('li');
      li.textContent = error;
      list.appendChild(li);
    });

    errorContainer.classList.add('is-visible');
  };

  const updateResultsVisibility = (container, shouldShow) => {
    const results = container.querySelector('[data-fpd-results]');
    if (!results) {
      return;
    }

    results.classList.toggle('is-hidden', !shouldShow);
  };

  const updateStat = (container, selector, value) => {
    const element = container.querySelector(selector);
    if (element) {
      element.textContent = value;
    }
  };

  const updateBars = ({ container, baselines, userResult, maxFpd }) => {
    const barsContainer = container.querySelector('[data-fpd-bars]');
    if (!barsContainer) {
      return;
    }

    baselines.forEach((baseline) => {
      const row = barsContainer.querySelector(`[data-fpd-bar="${baseline.id}"]`);
      if (!row) {
        return;
      }

      const labelEl = row.querySelector('[data-fpd-bar-label]');
      const fill = row.querySelector('[data-fpd-bar-fill]');
      const valueEl = row.querySelector('[data-fpd-bar-value]');

      if (labelEl) {
        labelEl.textContent = baseline.label;
      }

      const fvs = computeFvs(baseline.fpd, maxFpd);

      if (fill) {
        const scaleValue = clamp(fvs / 100, 0, 1);
        fill.style.transform = `scaleX(${scaleValue})`;
      }

      if (valueEl) {
        valueEl.textContent = `${formatPercent(fvs)} / 100`;
      }
    });

    const userBar = barsContainer.querySelector('[data-fpd-bar-user]');
    if (!userBar) {
      return;
    }

    if (!userResult) {
      userBar.classList.add('is-hidden');
      return;
    }

    const fill = userBar.querySelector('[data-fpd-bar-fill]');
    const valueEl = userBar.querySelector('[data-fpd-bar-value]');

    const fvs = computeFvs(userResult.fpd, maxFpd);

    if (fill) {
      const scaleValue = clamp(fvs / 100, 0, 1);
      fill.style.transform = `scaleX(${scaleValue})`;
    }

    if (valueEl) {
      valueEl.textContent = `${formatPercent(fvs)} / 100`;
    }

    userBar.classList.remove('is-hidden');
  };

  const updateTable = ({ container, baselines, maxFpd, userResult }) => {
    baselines.forEach((baseline) => {
      const fvs = computeFvs(baseline.fpd, maxFpd);
      const fpdCell = container.querySelector(`[data-fpd-row-fpd="${baseline.id}"]`);
      const fvsCell = container.querySelector(`[data-fpd-row-fvs="${baseline.id}"]`);
      const labelCell = container.querySelector(`[data-fpd-row-label="${baseline.id}"]`);
      if (labelCell) {
        labelCell.textContent = baseline.label;
      }
      if (fpdCell) {
        fpdCell.textContent = formatLargeNumber(baseline.fpd);
      }
      if (fvsCell) {
        fvsCell.textContent = `${formatPercent(fvs)} / 100`;
      }
    });

    const userRow = container.querySelector('[data-fpd-row-user]');
    if (!userRow) {
      return;
    }

    if (!userResult) {
      userRow.classList.add('is-hidden');
      return;
    }

    const fvs = computeFvs(userResult.fpd, maxFpd);
    const fpdCell = userRow.querySelector('[data-fpd-row-fpd]');
    const fvsCell = userRow.querySelector('[data-fpd-row-fvs]');

    if (fpdCell) {
      fpdCell.textContent = formatLargeNumber(userResult.fpd);
    }

    if (fvsCell) {
      fvsCell.textContent = `${formatPercent(fvs)} / 100`;
    }

    userRow.classList.remove('is-hidden');
  };

  const updateComparisonStatement = ({ container, baselines, userResult }) => {
    const comparisonEl = container.querySelector('[data-fpd-comparison]');
    const select = container.querySelector('[data-fpd-compare]');

    if (!comparisonEl || !select) {
      return;
    }

    const selectedId = select.value;
    const baseline = baselines.find((item) => item.id === selectedId && item.fpd > 0);

    if (!userResult || !baseline) {
      comparisonEl.textContent = 'Complete the calculator to see how your scenario compares.';
      return;
    }

    if (!Number.isFinite(userResult.fpd) || userResult.fpd <= 0) {
      comparisonEl.textContent = 'Enter values to compute a comparison.';
      return;
    }

    const ratio = userResult.fpd / baseline.fpd;

    if (!Number.isFinite(ratio) || ratio <= 0) {
      comparisonEl.textContent = 'Enter values to compute a comparison.';
      return;
    }

    const ratioMagnitude = ratio >= 1 ? ratio : 1 / ratio;
    const formattedRatio = ratioMagnitude >= 10
      ? formatLargeNumber(ratioMagnitude)
      : numberFormatter({ maximumFractionDigits: 1 }).format(ratioMagnitude);

    if (ratio >= 1) {
      comparisonEl.textContent = `This scenario delivers ${formattedRatio}× more fun-efficiency per dollar than ${baseline.label}.`;
    } else {
      comparisonEl.textContent = `This scenario delivers ${formattedRatio}× less fun-efficiency per dollar than ${baseline.label}.`;
    }
  };

  const drawRadarChart = ({ container, userResult, baseline }) => {
    const chartContainer = container.querySelector('[data-fpd-radar-chart]');
    if (!chartContainer) return;

    const gridGroup = chartContainer.querySelector('[data-fpd-radar-grid]');
    const dataGroup = chartContainer.querySelector('[data-fpd-radar-data]');
    const labelsGroup = chartContainer.querySelector('[data-fpd-radar-labels]');

    if (!gridGroup || !dataGroup || !labelsGroup) return;

    gridGroup.innerHTML = '';
    dataGroup.innerHTML = '';
    labelsGroup.innerHTML = '';

    const centerX = 200;
    const centerY = 200;
    const radius = 150;
    const levels = 5;
    const angleStep = (Math.PI * 2) / 5;

    const axes = [
      { label: 'Hours', key: 'engagementHours', max: 300 },
      { label: 'People', key: 'participants', max: 10 },
      { label: 'Reuse', key: 'reuseFactor', max: 20 },
      { label: 'Sensory', key: 'sensoryDepth', max: 10 },
      { label: 'Value', key: 'fpd', max: 1000 },
    ];

    for (let level = 1; level <= levels; level++) {
      const levelRadius = (radius / levels) * level;
      const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
      const points = [];

      for (let i = 0; i < axes.length; i++) {
        const angle = i * angleStep - Math.PI / 2;
        const x = centerX + Math.cos(angle) * levelRadius;
        const y = centerY + Math.sin(angle) * levelRadius;
        points.push(`${x},${y}`);
      }

      polygon.setAttribute('points', points.join(' '));
      polygon.setAttribute('fill', 'none');
      polygon.setAttribute('stroke', 'rgba(148, 163, 184, 0.2)');
      polygon.setAttribute('stroke-width', '1');
      gridGroup.appendChild(polygon);
    }

    axes.forEach((axis, i) => {
      const angle = i * angleStep - Math.PI / 2;
      const x = centerX + Math.cos(angle) * radius;
      const y = centerY + Math.sin(angle) * radius;

      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', centerX);
      line.setAttribute('y1', centerY);
      line.setAttribute('x2', x);
      line.setAttribute('y2', y);
      line.setAttribute('stroke', 'rgba(148, 163, 184, 0.3)');
      line.setAttribute('stroke-width', '1');
      gridGroup.appendChild(line);

      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', x + Math.cos(angle) * 20);
      text.setAttribute('y', y + Math.sin(angle) * 20);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('dominant-baseline', 'middle');
      text.setAttribute('fill', 'rgba(226, 232, 240, 0.8)');
      text.setAttribute('font-size', '12');
      text.textContent = axis.label;
      labelsGroup.appendChild(text);
    });

    const drawDataPolygon = (data, color, opacity = 0.3) => {
      const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
      const points = [];

      axes.forEach((axis, i) => {
        const value = data[axis.key] || 0;
        const normalizedValue = Math.min(value / axis.max, 1);
        const angle = i * angleStep - Math.PI / 2;
        const distance = normalizedValue * radius;
        const x = centerX + Math.cos(angle) * distance;
        const y = centerY + Math.sin(angle) * distance;
        points.push(`${x},${y}`);
      });

      polygon.setAttribute('points', points.join(' '));
      polygon.setAttribute('fill', color);
      polygon.setAttribute('fill-opacity', opacity);
      polygon.setAttribute('stroke', color);
      polygon.setAttribute('stroke-width', '2');
      dataGroup.appendChild(polygon);
    };

    if (baseline) {
      drawDataPolygon(baseline, 'rgba(148, 163, 184, 0.8)', 0.2);
    }

    if (userResult) {
      drawDataPolygon(userResult, 'rgba(96, 165, 250, 0.9)', 0.3);
    }
  };

  const initSection = (container) => {
    const sectionId = container.dataset.sectionId;

    if (instances.has(sectionId)) {
      instances.get(sectionId).destroy();
      instances.delete(sectionId);
    }

    const configEntries = parseBaselineConfig(container);
    const datasetUrl = container.dataset.baselinesUrl;
    let { baselines, datasetInfo } = buildBaselines({ configEntries, dataset: null });

    const form = container.querySelector('[data-fpd-form]');
    const resetButton = container.querySelector('[data-fpd-reset]');
    const comparisonSelect = container.querySelector('[data-fpd-compare]');
    const advancedCheckbox = container.querySelector('[data-fpd-field="useAdvancedModifiers"]');
    const advancedFields = container.querySelector('[data-fpd-advanced-fields]');
    let lastUserResult = null;

    const handleAdvancedToggle = () => {
      if (advancedFields) {
        const isChecked = advancedCheckbox?.checked;
        advancedFields.style.display = isChecked ? 'block' : 'none';
      }
    };

    handleAdvancedToggle();

    function renderBaselineState(userResultParam) {
      const userResult =
        typeof userResultParam === 'undefined' ? lastUserResult : userResultParam;

      if (typeof userResultParam !== 'undefined') {
        lastUserResult = userResult;
      }

      if (comparisonSelect) {
        const hasBaselines = baselines.length > 0;
        comparisonSelect.disabled = !hasBaselines;
        if (hasBaselines) {
          const exists = baselines.some((item) => item.id === comparisonSelect.value);
          if (!exists) {
            comparisonSelect.value = baselines[0].id;
          }
        }
      }

      const selectedId = comparisonSelect?.value;
      let baseline = baselines.find((item) => item.id === selectedId);
      if (!baseline && baselines.length) {
        baseline = baselines[0];
      }

      const allFpds = baselines.map((item) => item.fpd);
      if (userResult?.fpd) {
        allFpds.push(userResult.fpd);
      }

      const maxFpd = Math.max(...allFpds, 0);

      updateBars({ container, baselines, userResult, maxFpd });
      updateTable({ container, baselines, userResult, maxFpd });
      updateComparisonStatement({ container, baselines, userResult });
      updateDatasetPanel({ container, datasetInfo, baseline });

      drawRadarChart({
        container,
        userResult: userResult ? { ...userResult.metrics, fpd: userResult.fpd } : null,
        baseline: baseline ? { ...baseline.metrics, fpd: baseline.fpd } : null,
      });

      return maxFpd;
    }

    const applyBaselineData = (data) => {
      baselines = data.baselines;
      datasetInfo = data.datasetInfo;
      updateBaselineLabels(container, baselines);
      renderBaselineState();
    };

    applyBaselineData({ baselines, datasetInfo });

    if (datasetUrl) {
      fetchBaselineDataset(datasetUrl).then((dataset) => {
        if (!dataset) {
          return;
        }
        applyBaselineData(buildBaselines({ configEntries, dataset }));
      });
    }

    const handleSubmit = (event) => {
      event.preventDefault();

      if (!form) {
        return;
      }

      const metrics = readMetricsFromForm(form);
      const errors = validateMetrics(metrics);

      updateErrorState(container, errors);

      if (errors.length) {
        updateResultsVisibility(container, false);
        return;
      }

      const fpd = computeFpd(metrics);
      const userResult = {
        fpd,
        metrics,
        modifiers: {
          noveltyDecay: metrics.noveltyDecay,
          groupSynergy: metrics.groupSynergy,
          useAdvanced: metrics.useAdvancedModifiers,
        },
      };

      const maxFpd = renderBaselineState(userResult);
      const fvs = computeFvs(fpd, maxFpd);

      updateStat(
        container,
        '[data-fpd-score]',
        `${formatLargeNumber(fpd)} index points`
      );
      updateStat(
        container,
        '[data-fpd-fvs]',
        `${formatPercent(fvs)} / 100`
      );

      updateResultsVisibility(container, true);
    };

    const handleReset = () => {
      if (form) {
        form.reset();
      }
      lastUserResult = null;
      updateErrorState(container, []);
      updateResultsVisibility(container, false);
      renderBaselineState();
    };

    const handleComparisonChange = () => {
      renderBaselineState();
    };

    form?.addEventListener('submit', handleSubmit);
    resetButton?.addEventListener('click', handleReset);
    comparisonSelect?.addEventListener('change', handleComparisonChange);
    advancedCheckbox?.addEventListener('change', handleAdvancedToggle);

    instances.set(sectionId, {
      destroy: () => {
        form?.removeEventListener('submit', handleSubmit);
        resetButton?.removeEventListener('click', handleReset);
        comparisonSelect?.removeEventListener('change', handleComparisonChange);
        advancedCheckbox?.removeEventListener('change', handleAdvancedToggle);
      },
    });
  };

  const initAllSections = (root = document) => {
    root.querySelectorAll(SECTION_SELECTOR).forEach((section) => initSection(section));
  };

  document.addEventListener('DOMContentLoaded', () => {
    initAllSections(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target?.querySelector?.(SECTION_SELECTOR) || event.target;
    if (section && section.matches(SECTION_SELECTOR)) {
      initAllSections(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) {
      return;
    }
    const instance = instances.get(sectionId);
    if (instance?.destroy) {
      instance.destroy();
      instances.delete(sectionId);
    }
  });
})();
